import PropTypes from 'prop-types'
import { ComponentType, FC } from 'react'
import { User } from './AuthenticatedUser.context'
import { Permission } from './permissions.constants'
import { AuthenticatedUserResult, useAuthenticatedUser } from './useAuthenticatedUser'
import { PAGE_PERMISSIONS_MAP } from './pagePermissions.constants'

export const authenticatedUserShape = PropTypes.shape({
  user: PropTypes.shape({
    permissions: PropTypes.arrayOf(PropTypes.string).isRequired,
  }),
  hasPermission: PropTypes.func.isRequired,
  hasAnyPermission: PropTypes.func.isRequired,
  hasAllPermissions: PropTypes.func.isRequired,
  canAccessRoutes: PropTypes.func.isRequired,
})

export const userHasPermission = (user: User | null) => (permission: Permission): boolean =>
  !user?.permissions || user.permissions.length < 1 ? false : user.permissions.includes(permission)

export const userHasAnyPermission = (user: User | null) => (permissions: readonly Permission[] = []): boolean =>
  !permissions || permissions.length < 1 ? true : permissions.some(permission => userHasPermission(user)(permission))

export const userHasAllPermissions = (user: User | null) => (permissions: readonly Permission[] = []): boolean =>
  !permissions || permissions.length < 1 ? true : permissions.every(permission => userHasPermission(user)(permission))

export const userCanAccessRoute = (user: User | null) => (route: string): boolean => PAGE_PERMISSIONS_MAP.has(route) ? userHasAnyPermission(user)(PAGE_PERMISSIONS_MAP.get(route) || []) : true

export const createAuthenticatedUser = (user: User | null): AuthenticatedUserResult => ({
  user,
  hasPermission: userHasPermission(user),
  hasAnyPermission: userHasAnyPermission(user),
  hasAllPermissions: userHasAllPermissions(user),
  canAccessRoute: userCanAccessRoute(user),
})

export interface WithAuthenticatedUserProps {
  authenticatedUser: AuthenticatedUserResult,
}

// Using named functions to avoid "Component definition is missing display name" error
// https://github.com/yannickcr/eslint-plugin-react/issues/597#issuecomment-407567001
export default function withAuthenticatedUser<P>(ComposedComponent: ComponentType<P & WithAuthenticatedUserProps>): FC<P> {
  const WrappedConsumer: FC<P> = props => {
    const authenticatedUser = useAuthenticatedUser()
    return <ComposedComponent {...props} authenticatedUser={authenticatedUser} />
  }
  return WrappedConsumer
}
